import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import {
  type DispatchContext,
  publishStreamingMessage,
  subscribe,
} from '../sse';
import { createActionTurn, flushActionTurn } from '../sse/actionTurn';
import type { StreamVisualizationBufferEntry } from '../sse/dispatchStreamEvent';
import { SidekickHttpError } from '../sse/sidekickHttp';
import {
  clearStreamingContent,
  setIsStreaming,
  setToolActivity,
} from '../state/aiSidekickChatSlice';
import type { SidekickError } from '../state/chatTypes';
import { SYNTHETIC_TRANSPORT_ERROR_CODE } from '../state/constants';
import { drainSidekickEventStream } from './useSidekickEventStream.drain';
import { trackPendingPublish } from './useSidekickEventStream.publish';
import type {
  StartStreamInput,
  UseSidekickEventStreamParams,
  UseSidekickEventStreamResult,
} from './useSidekickEventStream.types';

/**
 * Drives one SSE turn: subscribe, publish, then drain through `done`.
 * Recoverable errors resubscribe once using the latest event id.
 * Authentication uses `refreshTokenHandler` only.
 */
export const useSidekickEventStream = (
  params: UseSidekickEventStreamParams
): UseSidekickEventStreamResult => {
  const { conversationId, apiBaseUrl, onError, onDone } = params;
  const dispatch = useDispatch();

  const abortRef = useRef<AbortController | null>(null);
  const turnIdRef = useRef(0);
  const stoppedTurnIdRef = useRef<number | null>(null);
  const pendingPublishRef = useRef<Promise<unknown> | null>(null);
  const streamingContentRef = useRef<string>('');
  const visualizationBufferRef = useRef<StreamVisualizationBufferEntry[]>([]);
  // Visualization frames lack ids, so tables attach to finalized messages FIFO.
  const finalizedMessagesAwaitingVisualizationRef = useRef<Set<string>>(
    new Set()
  );
  const lastEventIdRef = useRef<string | undefined>(undefined);
  const actionTurnRef = useRef(createActionTurn());
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const clearStreamLifecycleFlags = useCallback((): void => {
    dispatch(setIsStreaming(false));
  }, [dispatch]);

  const clearPartialStreamState = useCallback((): void => {
    dispatch(clearStreamingContent());
    dispatch(setToolActivity(null));
    streamingContentRef.current = '';
    visualizationBufferRef.current = [];
    finalizedMessagesAwaitingVisualizationRef.current = new Set();
  }, [dispatch]);

  const abort = useCallback((): void => {
    // A stopped turn's question stays open until it is acknowledged, so its
    // stop can still reach the backend; stop() closes it after that.
    const isStopAwaitingAck =
      pendingPublishRef.current !== null &&
      stoppedTurnIdRef.current === turnIdRef.current;
    if (!isStopAwaitingAck) {
      abortRef.current?.abort();
    }
    abortRef.current = null;
    actionTurnRef.current = createActionTurn();
    clearPartialStreamState();
    clearStreamLifecycleFlags();
  }, [clearPartialStreamState, clearStreamLifecycleFlags]);

  const start = useCallback(
    async (input: StartStreamInput): Promise<void> => {
      const targetConversationId = input.conversationId ?? conversationId;
      if (targetConversationId === null) {
        throw new Error('Sidekick SSE: cannot start without conversationId');
      }
      abort();
      // A stop still waiting on an earlier turn's question kept its own copy,
      // so this turn's stop must not wait on that question too.
      pendingPublishRef.current = null;
      lastEventIdRef.current = undefined;
      streamingContentRef.current = '';
      visualizationBufferRef.current = [];
      finalizedMessagesAwaitingVisualizationRef.current = new Set();
      const myTurnId = turnIdRef.current + 1;
      turnIdRef.current = myTurnId;
      const controller = new AbortController();
      abortRef.current = controller;
      const ownsController = (): boolean =>
        turnIdRef.current === myTurnId && abortRef.current === controller;
      const isCurrentTurn = (): boolean =>
        ownsController() &&
        !controller.signal.aborted &&
        stoppedTurnIdRef.current !== myTurnId;
      let shouldClearLifecycleFlags = true;
      const reportTransportError = (error: SidekickError): void => {
        if (isCurrentTurn()) {
          shouldClearLifecycleFlags = onErrorRef.current?.(error) !== true;
        }
      };

      dispatch(setIsStreaming(true));

      const ctx: DispatchContext = {
        dispatch,
        isActive: isCurrentTurn,
        streamingContentRef,
        visualizationBufferRef,
        finalizedMessagesAwaitingVisualizationRef,
        lastEventIdRef,
        actionTurnRef,
        onDone: (traceId) => {
          if (isCurrentTurn()) {
            onDoneRef.current?.(traceId);
          }
        },
        onError: (err) => {
          reportTransportError(err);
        },
      };

      let activeReader: ReadableStreamDefaultReader<Uint8Array> | undefined;
      let activeInitialChunk: Uint8Array | undefined;
      try {
        const subscribed = await subscribe({
          apiBaseUrl,
          conversationId: targetConversationId,
          lastEventId: lastEventIdRef.current,
          signal: controller.signal,
        });
        activeReader = subscribed.reader;
        activeInitialChunk = subscribed.initialChunk;
        if (!isCurrentTurn()) {
          await activeReader.cancel().catch(() => undefined);
          return;
        }

        await trackPendingPublish(
          pendingPublishRef,
          publishStreamingMessage({
            ...input,
            apiBaseUrl,
            conversationId: targetConversationId,
            signal: controller.signal,
          })
        );
        if (!isCurrentTurn()) {
          await activeReader.cancel().catch(() => undefined);
          return;
        }
      } catch (err) {
        if (activeReader !== undefined) {
          await activeReader.cancel().catch(() => undefined);
        }
        if (ownsController()) {
          if (!controller.signal.aborted) {
            if (err instanceof SidekickHttpError) {
              reportTransportError(err.sidekickError);
            } else {
              reportTransportError({
                code: SYNTHETIC_TRANSPORT_ERROR_CODE,
                message: err instanceof Error ? err.message : String(err),
              });
            }
          }
          if (shouldClearLifecycleFlags) {
            clearStreamLifecycleFlags();
          }
          abortRef.current = null;
        }
        return;
      }

      void (async () => {
        try {
          await drainSidekickEventStream({
            apiBaseUrl,
            conversationId: targetConversationId,
            controller,
            activeStream: {
              reader: activeReader,
              initialChunk: activeInitialChunk,
            },
            ctx,
            dispatch,
            clearPartialStreamState,
            onError: reportTransportError,
          });
        } finally {
          if (ownsController()) {
            if (shouldClearLifecycleFlags) {
              clearStreamLifecycleFlags();
            }
            abortRef.current = null;
          }
        }
      })().catch((err) => {
        if (import.meta.env.DEV) {
          console.warn(
            '[useSidekickEventStream] drain rejected unexpectedly',
            err
          );
        }
      });
    },
    [
      conversationId,
      apiBaseUrl,
      dispatch,
      abort,
      clearStreamLifecycleFlags,
      clearPartialStreamState,
    ]
  );

  const stop = useCallback(async (): Promise<void> => {
    // The stopped turn's own controller, even if cleanup or a newer turn has
    // since moved on from it.
    const controller = abortRef.current;
    stoppedTurnIdRef.current = turnIdRef.current;
    // Actions the backend already ran stay visible after the stop.
    flushActionTurn(actionTurnRef, dispatch);
    clearPartialStreamState();
    clearStreamLifecycleFlags();
    const pendingPublish = pendingPublishRef.current;
    if (pendingPublish !== null) {
      await pendingPublish.catch(() => undefined);
    }
    controller?.abort();
    if (abortRef.current === controller) {
      abortRef.current = null;
    }
  }, [clearPartialStreamState, clearStreamLifecycleFlags, dispatch]);

  useEffect(() => abort, [abort]);

  return useMemo(() => ({ start, abort, stop }), [start, abort, stop]);
};
